import { useEffect, useRef, useState } from "react";
import { useAuth, useClerk, useSession } from "@clerk/react";
import { useLocation } from "react-router-dom";
import { AlertDialog, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

// ahu_idle_scale (localStorage) speeds the clock up for testing only.
export const MIN = 60 * 1000 / (Number(localStorage.getItem("ahu_idle_scale")) || 1);
const EVENTS = ["pointerdown", "keydown", "scroll", "touchstart", "input"];
const SHARED = "ahu_last_active";

// Shared=true keeps every open AHU tab on one clock via localStorage.
export const useIdle = (warnMs, endMs, onEnd, { enabled = true, shared = false, startedAt = 0 } = {}) => {
  const [warn, setWarn] = useState(false);
  const last = useRef(Date.now());
  const end = useRef(onEnd);
  end.current = onEnd;
  const warnRef = useRef(false);
  warnRef.current = warn;

  useEffect(() => {
    if (!enabled) return undefined;
    const mark = () => { last.current = Date.now(); if (shared) localStorage.setItem(SHARED, String(last.current)); };
    const seenAt = () => (shared ? Math.max(last.current, Number(localStorage.getItem(SHARED)) || 0) : last.current);
    const check = () => {
      const idle = Date.now() - seenAt();
      if (idle >= endMs) { setWarn(false); end.current(); last.current = Date.now(); return true; }
      setWarn(idle >= warnMs);
      return false;
    };
    // A paused tab (phone locked, app switched) must not let the first tap back reset an expired clock.
    const bump = () => { if (!check() && !warnRef.current && Date.now() - last.current > 1000) mark(); };
    // No saved activity: fall back to when the session began, so long-signed-in devices still expire.
    const stored = Number(localStorage.getItem(SHARED)) || startedAt;
    if (shared && stored && Date.now() - stored >= endMs) { end.current(); return undefined; }
    mark();
    const onVisible = () => { if (document.visibilityState === "visible") check(); };
    EVENTS.forEach((e) => window.addEventListener(e, bump, { passive: true, capture: true }));
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    const t = setInterval(check, 1000);
    return () => {
      EVENTS.forEach((e) => window.removeEventListener(e, bump, { capture: true }));
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
      clearInterval(t);
    };
  }, [enabled, shared, warnMs, endMs, startedAt]);

  const keep = () => { last.current = Date.now(); if (shared) localStorage.setItem(SHARED, String(last.current)); setWarn(false); };
  return [warn, keep];
};

export const IdleNotice = ({ open, title, body, label, onKeep, testId }) => (
  <AlertDialog open={open}>
    <AlertDialogContent className="rounded-2xl" data-testid={testId}>
      <AlertDialogHeader>
        <AlertDialogTitle className="font-display text-2xl font-normal">{title}</AlertDialogTitle>
        {body && <AlertDialogDescription className="text-[15px] text-ink">{body}</AlertDialogDescription>}
      </AlertDialogHeader>
      <AlertDialogFooter>
        <button className="btn-charcoal w-full sm:w-auto" onClick={onKeep} data-testid={`${testId}-keep-btn`}>{label}</button>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);


export const IdleSignOut = () => {
  const { isSignedIn } = useAuth();
  const { signOut } = useClerk();
  const { session } = useSession();
  const startedAt = session?.createdAt ? new Date(session.createdAt).getTime() : 0;
  useEffect(() => {
    if (isSignedIn) sessionStorage.removeItem("ahu_idle_signout");
    if (isSignedIn === false) localStorage.removeItem(SHARED);
  }, [isSignedIn]);
  const [warn, keep] = useIdle(3 * MIN, 5 * MIN, async () => {
    sessionStorage.setItem("ahu_idle_signout", "1");
    localStorage.removeItem(SHARED);
    await signOut({ redirectUrl: "/sign-in" });
  }, { enabled: !!isSignedIn && !!startedAt, shared: true, startedAt });
  const inQuestions = useLocation().pathname.startsWith("/questions");
  return <IdleNotice open={!!isSignedIn && warn} title={inQuestions ? "Are you still there?" : "Still there?"}
    body="For your privacy, AHU will sign you out in 2 minutes." label={inQuestions ? "Keep going" : "Stay signed in"} onKeep={keep} testId="idle-signout-dialog" />;
};
